import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  DatePicker,
  Button,
  Space,
  Spin,
  Statistic,
  Table,
  Typography,
  List,
  Divider,
  Progress,
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  RiseOutlined,
  GiftOutlined,
  FileTextOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { fetchManagerFinances } from '../utils/fetch';
import config from '../../../config.json';

const { Text } = Typography;

const { RangePicker } = DatePicker;

/**
 * Компонент финансов для менеджеров
 * Отображает: выручку, комиссию, бонусы, количество сделок, средний чек
 */
const ManagerFinances = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment().endOf('day'),
  ]);

  const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
  const endDate = dateRange?.[1]?.format('YYYY-MM-DD');

  const formatCurrency = useCallback((value = 0) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'currency',
      currency: 'KZT',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0);
  }, []);

  const formatPercent = useCallback((value = 0) => {
    const formatted = Number.isFinite(value) ? value : 0;
    return `${formatted.toFixed(2)}%`;
  }, []);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const result = await fetchManagerFinances(startDate, endDate);
      setData(result);
    } catch (error) {
      console.error('Ошибка загрузки финансов:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const setQuickRange = (type) => {
    switch (type) {
      case 'today':
        setDateRange([moment().startOf('day'), moment().endOf('day')]);
        break;
      case 'week':
        setDateRange([moment().subtract(6, 'days').startOf('day'), moment().endOf('day')]);
        break;
      case 'month':
        setDateRange([moment().startOf('month'), moment().endOf('day')]);
        break;
      default:
        break;
    }
  };

  const summaryCards = useMemo(() => {
    if (!data?.summary) return [];
    const { summary } = data;
    return [
      {
        key: 'revenue',
        title: 'Общая выручка',
        value: summary.totalRevenue,
        icon: <DollarOutlined />,
      },
      {
        key: 'bonus',
        title: 'Бонусы',
        value: summary.bonus,
        icon: <GiftOutlined />,
      },
      {
        key: 'deals',
        title: 'Количество сделок',
        value: summary.deals,
        icon: <FileTextOutlined />,
        format: (v) => v,
      },
      {
        key: 'avg',
        title: 'Средний чек',
        value: summary.avgDeal,
        icon: <LineChartOutlined />,
      },
    ];
  }, [data?.summary]);

  const columns = useMemo(
    () => [
      {
        title: 'Дата',
        dataIndex: 'date',
        key: 'date',
        width: 110,
      },
      {
        title: 'Клиент',
        dataIndex: 'client',
        key: 'client',
        width: 200,
        render: (text, record) => (
          <Typography.Link
            onClick={() => {
              if (record.clientId) {
                navigate(`/client/${record.clientId}`);
              }
            }}
            style={{ color: '#1890ff', cursor: record.clientId ? 'pointer' : 'default' }}
          >
            {text}
          </Typography.Link>
        ),
      },
      {
        title: 'Номер договора',
        dataIndex: 'contractNumber',
        key: 'contractNumber',
        width: 220,
        render: (text, record) => {
          if (!record.leadId) {
            return text || '-';
          }
          return (
            <a
              href={`${config.service}/api/file/agreement/${record.leadId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text || '-'}
            </a>
          );
        },
      },
      {
        title: 'Сумма',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Бонус',
        dataIndex: 'bonus',
        key: 'bonus',
        width: 120,
        render: () => null,
      },
    ],
    [formatCurrency, navigate]
  );

  const financeRows = useMemo(
    () =>
      (data?.deals || []).map((item, idx) => ({
        key: item.id || `deal-${idx}`,
        ...item,
      })),
    [data?.deals]
  );

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Загрузка данных..." />
      </div>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 24 }}>
        <Space size="middle" direction="vertical" style={{ width: '100%' }}>
          <Space
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            <Text strong style={{ fontSize: 16 }}>
              Мои финансы - Менеджер
            </Text>
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={loadData}
              loading={loading}
            >
              Обновить
            </Button>
          </Space>
          <Space size="small" wrap>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates)}
              format="DD.MM.YYYY"
              allowClear={false}
            />
            <Button onClick={() => setQuickRange('today')}>Сегодня</Button>
            <Button onClick={() => setQuickRange('week')}>Неделя</Button>
            <Button onClick={() => setQuickRange('month')}>Месяц</Button>
          </Space>
        </Space>
      </Card>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {summaryCards.map((card) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={card.key}>
              <Card bordered>
                <Space align="start" size="large">
                  {card.icon}
                  <Statistic
                    title={card.title}
                    value={card.value || 0}
                    formatter={
                      card.format ? card.format : (value) => formatCurrency(value)
                    }
                    valueStyle={{ fontSize: 20 }}
                  />
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card title="📈 Доходы">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {(data?.incomes || []).map((item) => {
                  const maxAmount = Math.max(...(data?.incomes || []).map(i => i.amount));
                  const percent = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0;
                  const color = item.key === 'revenue' ? '#1890ff' : '#52c41a';

                  return (
                    <div key={item.key}>
                      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text strong>{item.name}</Text>
                        <Text strong style={{ fontSize: 16, color }}>
                          {formatCurrency(item.amount)}
                        </Text>
                      </Space>
                      <Progress
                        percent={percent}
                        showInfo={false}
                        strokeColor={color}
                        strokeWidth={12}
                      />
                    </div>
                  );
                })}
                {(!data?.incomes || data.incomes.length === 0) && (
                  <Text type="secondary">Нет данных</Text>
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        <Card
          title="📋 Мои сделки"
        >
          <Table
            dataSource={financeRows}
            columns={columns}
            size="small"
            bordered
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Всего сделок: ${total}`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50'],
            }}
            scroll={{ x: 900 }}
            locale={{ emptyText: '📭 Нет сделок за выбранный период' }}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default ManagerFinances;

